import { useGT } from 'gt-react';
import type { CompositionGroup } from './compositionDraft';

type Translate = ReturnType<typeof useGT>;

export function groupTitle(group: CompositionGroup, gt: Translate): string {
  switch (group) {
    case 'minimum':
      return gt('Minimum', { context: 'surplus settings section' });
    case 'deduction':
      return gt('Deductions', { context: 'surplus settings section' });
    default:
      return gt('Retained protection', { context: 'surplus settings section' });
  }
}

/** How the group's selected amounts combine; shown beside the title. */
export function groupOperatorPhrase(
  group: CompositionGroup,
  gt: Translate,
): string {
  switch (group) {
    case 'minimum':
      return gt('largest of selected inputs and floor', {
        context: 'surplus minimum operator',
      });
    case 'deduction':
      return gt('sum of selected', { context: 'surplus deduction operator' });
    default:
      return gt('largest of selected', {
        context: 'surplus protection operator',
      });
  }
}

export function addToGroupLabel(
  group: CompositionGroup,
  gt: Translate,
): string {
  switch (group) {
    case 'minimum':
      return gt('Add minimum input', {
        context: 'open surplus minimum component choices',
      });
    case 'deduction':
      return gt('Add deduction', {
        context: 'open surplus deduction component choices',
      });
    default:
      return gt('Add retained protection', {
        context: 'open surplus retention component choices',
      });
  }
}

export function componentLabel(kind: string, gt: Translate): string {
  switch (kind) {
    case 'safety_stock':
      return gt('Base forecast safety stock', {
        context: 'surplus composition component',
      });
    case 'calculated_minimum':
      return gt('Calculated minimum', {
        context: 'unsupported surplus composition component',
      });
    case 'override_floor':
      return gt('Active minimum floor', {
        context: 'surplus composition component',
      });
    case 'existing_commitment_bundle':
      return gt('Existing commitments (compatibility)', {
        context: 'surplus composition component',
      });
    case 'snapshot_reservations':
      return gt('Snapshot reservations', {
        context: 'surplus composition component',
      });
    case 'open_sales_order_demand':
      return gt('Open sales order demand', {
        context: 'surplus composition component',
      });
    case 'staged_unacknowledged_transfers':
      return gt('Staged unacknowledged transfers', {
        context: 'surplus composition component',
      });
    case 'dated_bookings':
      return gt('Dated bookings', {
        context: 'unsupported surplus composition component',
      });
    case 'undated_reservations':
      return gt('Undated reservations', {
        context: 'unsupported surplus composition component',
      });
    case 'historical_customer_sales':
      return gt('Customer sales coverage', {
        context: 'surplus composition component',
      });
    case 'composed_minimum':
      return gt('Composed minimum', {
        context: 'surplus composition component',
      });
    case 'existing_effective_trigger':
      return gt('Existing effective trigger (compatibility)', {
        context: 'surplus composition component',
      });
    default:
      return gt('Unknown component', {
        context: 'surplus composition component unavailable in this client',
      });
  }
}

export function componentDescription(kind: string, gt: Translate): string {
  switch (kind) {
    case 'safety_stock':
      return gt(
        'Base safety stock before human overrides. Comparison units must be verified.',
        { context: 'surplus composition source limitation' },
      );
    case 'calculated_minimum':
      return gt(
        'No authoritative calculated-minimum provider is established. Legacy minimum and reorder point are not substituted.',
        { context: 'surplus composition unsupported source' },
      );
    case 'override_floor':
      return gt(
        'Maximum with the active minimum floor. Confirmed absence contributes configured zero.',
        { context: 'surplus composition floor explanation' },
      );
    case 'existing_commitment_bundle':
      return gt(
        'Existing reservation, sales order and staged-transfer arithmetic, including its age, partial-shipment and acknowledgement limitations.',
        { context: 'surplus composition compatibility source' },
      );
    case 'dated_bookings':
    case 'undated_reservations':
      return gt(
        'Producer date, blank-date classification, remaining quantity and overlap evidence are not established.',
        { context: 'surplus composition unsupported booking source' },
      );
    case 'historical_customer_sales':
      return gt(
        'Observe 12 complete months; retain 1–24 months of customer sales. Covered periods and comparable units are required.',
        {
          context:
            'surplus composition sales observation and target distinction',
        },
      );
    case 'composed_minimum':
      return gt(
        'Use the minimum defined here from this same settings revision.',
        { context: 'surplus composition minimum dependency' },
      );
    case 'existing_effective_trigger':
      return gt(
        'Existing resolved trigger, including applicable overrides and floor.',
        { context: 'surplus composition compatibility trigger' },
      );
    default:
      return gt(
        'Existing source semantics apply. Comparable units and source evidence are required.',
        { context: 'surplus composition source limitation' },
      );
  }
}

export function traceStateLabel(state: string, gt: Translate): string {
  switch (state) {
    case 'not_evaluated':
      return gt('Not evaluated', {
        context: 'surplus configured minimum dependency intentionally not read',
      });
    case 'not_selected':
      return gt('Not selected', {
        context: 'surplus component omitted rather than zero',
      });
    case 'unavailable':
      return gt('Unavailable', {
        context: 'surplus component missing selected evidence',
      });
    case 'configured_zero':
      return gt('Configured zero', {
        context: 'surplus component deliberate zero protection',
      });
    case 'estimated':
      return gt('Estimated', {
        context: 'surplus component partial history estimate',
      });
    default:
      return gt('Available', { context: 'surplus component source status' });
  }
}

export function compositionWarning(message: string, gt: Translate): string {
  switch (message) {
    case 'Source stock quantity (unit unspecified); no conversion or cross-source comparison.':
      return gt(
        'Source stock quantity (unit unspecified). No conversion or cross-source comparison.',
        { context: 'surplus native stock warning' },
      );
    case 'No deductions selected; commitments are not protected.':
      return gt('No deductions selected; commitments are not protected.', {
        context: 'surplus composition warning',
      });
    case 'No retained protection selected.':
      return gt('No retained protection selected.', {
        context: 'surplus composition warning',
      });
    case 'No minimum protection selected; minimum is configured zero.':
      return gt('No minimum protection selected; minimum is configured zero.', {
        context: 'surplus composition warning',
      });
    case 'Deductions and sales coverage may conservatively protect the same future demand.':
      return gt(
        'Deductions and sales coverage may conservatively protect the same future demand.',
        { context: 'surplus composition warning' },
      );
    default:
      return gt(
        'Selected source evidence requires review. Missing data is not zero.',
        { context: 'surplus composition unavailable evidence warning' },
      );
  }
}

export function traceReason(reason: string, gt: Translate): string {
  switch (reason) {
    case 'minimum_not_evaluated':
      return gt('Not evaluated because surplus does not use this minimum.', {
        context: 'surplus unused minimum dependency explanation',
      });
    case 'no_snapshot':
      return gt('No inventory snapshot is available for this location.', {
        context: 'surplus physical stock unavailable reason',
      });
    case 'cross_location_quantity_unit_unverified':
      return gt(
        'Location quantities have no verified common unit. Per-location results are shown; their aggregate is unavailable.',
        { context: 'surplus incomparable location aggregate limitation' },
      );
    case 'aggregate_numeric_overflow':
      return gt(
        'The combined quantity exceeds the supported numeric range. The aggregate is unavailable.',
        { context: 'surplus numeric aggregate overflow limitation' },
      );
    case 'quantity_unit_unverified':
      return gt('Comparison units are not verified.', {
        context: 'surplus source unavailable reason',
      });
    case 'missing_commitments':
      return gt('Selected commitment data is unavailable.', {
        context: 'surplus source unavailable reason',
      });
    case 'missing_policy':
      return gt('Selected policy or minimum data is unavailable.', {
        context: 'surplus source unavailable reason',
      });
    case 'catchment_pending':
      return gt('The complete customer-sales catchment is unavailable.', {
        context: 'surplus source unavailable reason',
      });
    case 'history_unavailable':
      return gt('Customer-sales history or covered periods are unavailable.', {
        context: 'surplus source unavailable reason',
      });
    case 'no_minimum_protection_selected':
      return gt('No minimum protection selected; this is configured zero.', {
        context: 'surplus configured zero reason',
      });
    case 'covered_month_constant_rate_estimate':
      return gt(
        'Estimated at a constant monthly rate over the covered months. Actual surplus may differ.',
        { context: 'surplus partial history estimate reason' },
      );
    default:
      return gt(
        'Source quantities, coverage or compatibility require review.',
        { context: 'surplus source unavailable reason' },
      );
  }
}
